export const scaleRecipeComponents = (components, factor) =>
  components.map(component => ({
    ...component,
    quantity: Number((Number(component.quantity) * factor).toFixed(4)),
  }));

const OUTPUT_UNITS = {
  g: ["mass", 1], kg: ["mass", 1000],
  ml: ["volume", 1], l: ["volume", 1000], litre: ["volume", 1000],
  piece: ["count", 1], pieces: ["count", 1], pc: ["count", 1], pcs: ["count", 1],
  portion: ["portion", 1], portions: ["portion", 1],
  serving: ["portion", 1], servings: ["portion", 1],
};

export const outputQuantityBase = (quantity, unit) => {
  const match = OUTPUT_UNITS[String(unit || "").trim().toLowerCase()];
  const value = Number(quantity);
  return match && value > 0 ? { dimension: match[0], base: value * match[1] } : null;
};

export const outputQuantityInUnit = (base, unit) => {
  const one = outputQuantityBase(1, unit);
  return one ? Number(base) / one.base : null;
};

const ingredientFor = (component, ingredients) => (ingredients || []).find(item =>
  String(item.id || item._id) === String(component.component_id || component.ingredient_id),
);

export const theoreticalOutputIssues = (components = [], ingredients = []) => {
  const amounts = components.map(component => ({ component, amount: outputQuantityBase(component.quantity, component.unit) }));
  const dimensions = new Set(amounts.map(item => item.amount?.dimension).filter(Boolean));
  const needsMass = dimensions.has("mass") || dimensions.has("count");
  const issues = [];
  for (const { component, amount } of amounts) {
    const ingredient = ingredientFor(component, ingredients) || component;
    if (!amount || component.component_type === "prep_recipe") issues.push({ component, reason: component.component_type === "prep_recipe" ? "Prep Item output conversion is not defined" : "Unsupported component unit" });
    else if (amount.dimension === "count" && !(Number(ingredient.avg_piece_weight_g) > 0)) issues.push({ component, reason: "Average piece weight is required" });
    else if (needsMass && amount.dimension === "volume" && !(Number(ingredient.density_g_per_ml) > 0)) issues.push({ component, reason: "Density (g/ml) is required" });
    else if (amount.dimension === "portion" && dimensions.size > 1) issues.push({ component, reason: "Portions cannot be converted to a physical output" });
  }
  return issues;
};

// Produced Output is a calculated physical total. Never guess conversions.
export const theoreticalFinishedOutput = (components = [], ingredients = []) => {
  if (!components.length) return null;
  if (theoreticalOutputIssues(components, ingredients).length) return null;
  const amounts = components.map(component => ({ component, amount: outputQuantityBase(component.quantity, component.unit), ingredient: ingredientFor(component, ingredients) || component }));
  const dimensions = new Set(amounts.map(item => item.amount.dimension));
  if (dimensions.has("portion") && dimensions.size > 1) return null;
  const dimension = dimensions.has("mass") || dimensions.has("count") ? "mass" : dimensions.has("volume") ? "volume" : dimensions.has("portion") ? "portion" : null;
  if (!dimension) return null;
  let totalBase = 0;
  for (const { amount, ingredient } of amounts) {
    if (dimension === "mass") {
      if (amount.dimension === "mass") totalBase += amount.base;
      else if (amount.dimension === "volume") totalBase += amount.base * Number(ingredient.density_g_per_ml);
      else if (amount.dimension === "count") totalBase += amount.base * Number(ingredient.avg_piece_weight_g);
      else return null;
    } else {
      if (amount.dimension !== dimension) return null;
      totalBase += amount.base;
    }
  }
  const unit = { mass: "g", volume: "ml", portion: "portion" }[dimension];
  return unit ? { quantity: Number(totalBase.toFixed(4)), unit, dimension } : null;
};

export const autoFinishedOutputPatch = calculatedOutput => calculatedOutput
  ? { yield_quantity: calculatedOutput.quantity, yield_unit: calculatedOutput.unit }
  : { yield_quantity: "", yield_unit: "" };

// A variant specifies the finished output for one item. Counts are entered
// only when scaling or planning production, then summed into one master scale.
export const calculateVariantScaleTarget = (standardOutput, variants, counts = {}) => {
  const standard = outputQuantityBase(standardOutput.quantity, standardOutput.unit);
  if (!standard) return { standard: null, contributions: [], targetBase: 0, factor: null };
  const contributions = (variants || []).filter(variant => variant.active !== false).map(variant => {
    const count = Number(counts[variant.id] || 0);
    const portion = outputQuantityBase(variant.quantity, variant.unit);
    const valid = portion && portion.dimension === standard.dimension;
    return {
      variant, count,
      requiredBase: valid && count > 0 ? portion.base * count : 0,
      valid,
    };
  });
  const targetBase = contributions.reduce((sum, item) => sum + item.requiredBase, 0);
  return { standard, contributions, targetBase, factor: targetBase > 0 ? targetBase / standard.base : null };
};


// QPT receives an explicit selected-output context. This intentionally carries
// requested counts forward instead of deriving them again from the total.
export const normaliseVariantQptOutputContext = (standardOutput, variants, counts = {}) => {
  const calculated = calculateVariantScaleTarget(standardOutput, variants, counts);
  if (!calculated.standard || !(calculated.targetBase > 0)) return null;
  const unit = standardOutput.unit;
  return {
    mode: "scaled",
    total_output_quantity: outputQuantityInUnit(calculated.targetBase, unit),
    total_output_unit: unit,
    variants: calculated.contributions
      .filter(item => item.valid && item.count > 0)
      .map(item => ({
        id: item.variant.id,
        name: item.variant.name,
        per_piece_quantity: Number(item.variant.quantity),
        per_piece_unit: item.variant.unit,
        requested_count: item.count,
        contribution_quantity: outputQuantityInUnit(item.requiredBase, unit),
      })),
  };
};


// Produced output is the batch made; usable output is what can actually be
// portioned after recipe-level process loss.  Ingredient yield remains separate.
export const resolveUsableOutput = (producedOutput, variants = [], manualUsableOutput = null) => {
  const produced = outputQuantityBase(producedOutput?.quantity, producedOutput?.unit);
  if (!produced) return null;
  const manual = manualUsableOutput?.quantity !== "" && manualUsableOutput?.quantity != null
    ? outputQuantityBase(manualUsableOutput.quantity, manualUsableOutput.unit || producedOutput.unit)
    : null;
  const tolerance = Math.max(0.01, produced.base * 0.0001);
  const usableBase = manual?.base || produced.base;
  return {
    producedBase: produced.base,
    usableBase,
    quantity: outputQuantityInUnit(usableBase, producedOutput.unit),
    unit: producedOutput.unit,
    lossQuantity: outputQuantityInUnit(Math.max(0, produced.base - usableBase), producedOutput.unit),
    lossPercent: produced.base ? Math.max(0, (produced.base - usableBase) / produced.base * 100) : 0,
    hasStandardCounts: false,
    error: usableBase > produced.base + tolerance ? "Usable output cannot exceed Produced / Finished Output." : null,
  };
};

// Both Recipe Builder and approved-recipe operational preview use this exact driver rule.
export const productionDriverScaleFactor = (components, driverComponentId, availableAmount) => {
  const driver = (components || []).find(component => String(component.component_id || component.id) === String(driverComponentId));
  const sourceAmount = Number(driver?.quantity);
  const requestedAmount = Number(availableAmount);
  return sourceAmount > 0 && requestedAmount > 0 ? requestedAmount / sourceAmount : null;
};


// An explicit serving basis is persisted through the existing output fields.
// It is never inferred from ingredient totals, names or Menu associations.
export const hasServingOutput = recipe =>
  recipe?.is_prep_item !== true &&
  Number.isFinite(Number(recipe?.yield_quantity)) && Number(recipe?.yield_quantity) > 0 &&
  ["portion", "portions", "serving", "servings"].includes(String(recipe?.yield_unit || "").trim().toLowerCase());

export const isSingleServingRecipe = recipe =>
  hasServingOutput(recipe) && Number(recipe.yield_quantity) === 1;
